import { sendGTMEvent } from "@next/third-parties/google";
import type { ExperimentResult } from "@sa-nu/experiments/bucket";
import {
  useExperiments,
  useVisitorId,
} from "@sa-nu/experiments/experiment-context";
import { useMemo } from "react";

import type { AccommodationReservationProvider } from "../../__generated__/gql/graphql";

import { getProviderTypeName } from "./helpers/reservation";
import {
  getLeadTime,
  clearLeadTimeStorage,
  recordReservationFormEntryTimeIfNeeded,
} from "./helpers/reservation-form";

/**
 * ここで定義されているパラメータは、下記で管理されている。
 * https://docs.google.com/spreadsheets/d/1Xh-nKvGzOkRGogLRCG7WLjn9jjBYr0lOkp_ct_QpQos/edit?gid=94441744#gid=94441744
 *
 * 追加、変更の際には上記のスプレッドシートを更新すること。
 */

// イベントは任意の文字列ではなく、こちらで管理したものを送信すること。
// GTM の作法に従ってスネークケースで定義する。
// GA の制約で、40文字以下にすること。
type GtmEventType =
  // 予約完了時
  | "reservation_completed"
  // 予約更新時
  | "reservation_updated"
  // チェックイン
  | "checkin_completed"
  // チェックアウト
  | "checkout_completed"
  // 滞在レビュー投稿
  | "stay_review_submitted"
  // サブスクリプション申込完了時
  | "std_subscription_application_completed"
  // Trial サブスクリプション申込完了時
  | "trial_subscription_application_completed"
  // Co-Owners 仮申込完了時
  | "co_owners_pre_application_completed"
  // Co-Owners 本申込完了時
  | "co_owners_application_completed"
  // Trial 申し込みページ閲覧
  | "trial_subscription_apply_page_viewed"
  // Trial 利用開始月選択
  | "trial_subscription_start_date_selected"
  // Trial 氏名フィールドフォーカス
  | "trial_subscription_name_field_focused"
  // Trial 確認画面へ進むボタンクリック
  | "trial_subscription_draft_proceed_clicked"
  // Trial 利用規約チェックボックス変更
  | "trial_subscription_policy_checkbox_changed"
  // Trial メール受信同意チェックボックス変更
  | "trial_subscription_email_checkbox_changed"
  // Trial 申し込みボタンクリック
  | "trial_subscription_confirm_apply_clicked"
  // Trial 前の画面に戻るボタンクリック
  | "trial_subscription_confirm_back_clicked"
  // Trial スタートページ閲覧
  | "trial_subscription_start_page_viewed"
  // Trial スタートページ申し込みボタンクリック
  | "trial_subscription_start_proceed_clicked"
  // Trial 空室状況確認ボタンクリック
  | "trial_subscription_availability_clicked"
  // Trial Co-Ownersリンククリック
  | "trial_subscription_co_owners_link_clicked"
  // Trial 利用規約リンククリック（スタートページ）
  | "trial_subscription_start_terms_clicked"
  // Trial 個人情報の取り扱いリンククリック（スタートページ）
  | "trial_subscription_start_privacy_clicked"
  // Weekday スタートページ申し込みボタンクリック
  | "weekday_subscription_start_proceed_clicked"
  // Weekday 確認画面へ進むボタンクリック
  | "weekday_subscription_draft_proceed_clicked"
  // Weekday クレジットカード登録ボタンクリック
  | "weekday_subscription_card_register_clicked"
  // Weekday 申し込みボタンクリック
  | "weekday_subscription_confirm_apply_clicked"
  // 予約フォーム表示(通常)
  | "rsv_form_viewed"
  // 予約フォーム表示(予約変更)
  | "rsv_change_form_viewed"
  // 予約カレンダー: 日付カレンダーで日付を選択した
  | "rsv_cal_date_picked"
  // 予約カレンダー: シェブロンで日付移動した
  | "rsv_cal_nav_chevron_tapped"
  // 予約カレンダー: CICOモーダルで日付を確定した
  | "rsv_cal_cico_date_set"
  // 予約カレンダー: 絞り込みフィルターを表示した
  | "rsv_cal_filter_opened"
  // 予約カレンダー: エリアで絞り込んだ
  | "rsv_cal_area_filtered"
  // 予約カレンダー: 設備で絞り込んだ
  | "rsv_cal_facility_filtered"
  // 予約カレンダー: 建築タイプで絞り込んだ
  | "rsv_cal_arch_type_filtered"
  // 予約カレンダー: 空きマスをタップした
  | "rsv_cal_cell_selected"
  // 予約カレンダー: チェックアウト日を選択した
  | "rsv_cal_checkout_selected"
  // クレジットカード: 登録ダイアログを開いた
  | "card_register_started"
  // クレジットカード: ページ表示時に登録済みだった
  | "card_existing"
  // クレジットカード: 新規登録が完了した
  | "card_registered"
  // クレジットカード: 登録済み状態になった（既存 or 新規登録）
  | "card_ready"
  // 予約フォーム: 予約確定ボタンクリック（新規予約作成時）
  | "rsv_create_submit_clicked"
  // 部屋タイプ予約: 一覧(新UI)を表示した
  | "rt_rsv_list_viewed"
  // 部屋タイプ予約: 検索結果(在庫)が確定した（初回表示・絞り込み変更後の再取得のいずれも含む）
  | "rt_rsv_list_loaded"
  // 部屋タイプ予約: 一覧カードの「予約」CTA をタップした
  | "rt_rsv_card_reserve_clicked"
  // 部屋タイプ予約: 日程カレンダーの「予約する」CTA をタップした
  | "rt_rsv_calendar_reserve_clicked"
  // 部屋タイプ予約: 日程カレンダーでチェックイン日を選択した
  | "rt_rsv_checkin_selected"
  // 部屋タイプ予約: 日程カレンダーでチェックアウト日を選択した
  | "rt_rsv_checkout_selected"
  // 部屋タイプ予約: エリアで絞り込んだ
  | "rt_rsv_area_filtered"
  // 部屋タイプ予約: 日程で絞り込んだ
  | "rt_rsv_date_filtered"
  // 部屋タイプ予約: 設備で絞り込んだ
  | "rt_rsv_facility_filtered"
  // 部屋タイプ予約: 建築タイプで絞り込んだ
  | "rt_rsv_arch_type_filtered"
  // 部屋タイプ予約: 拠点名・拠点画像をタップし拠点詳細モーダルを開いた
  | "rt_rsv_site_detail_clicked"
  // 部屋タイプ予約: 拠点一覧拠点別カレンダーで週送りボタンをタップした
  | "rt_rsv_calendar_week_shifted"
  // 部屋タイプ予約: 拠点一覧拠点別カレンダーのセルをタップし拠点詳細モーダルを開いた
  | "rt_rsv_calendar_cell_clicked"
  // 部屋タイプ予約: 拠点詳細モーダルの「同じ日程で探す」ボタンをタップした
  | "rt_rsv_detail_same_dates_clicked";

/** GTMの`experiments`パラメータ用にシリアライズする。有効な実験が無ければパラメータ自体を含めない。 */
export function serializeExperimentsParam(experiments: ExperimentResult[]): {
  experiments?: string;
} {
  if (experiments.length === 0) {
    return {};
  }
  return {
    experiments: JSON.stringify(
      experiments.map(({ gaId, variant, thresholdPercent }) => ({
        id: gaId,
        v: variant === "treatment" ? "t" : "c",
        th: thresholdPercent,
      })),
    ),
  };
}

/**
 * GTMの`visitor_id`パラメータ用にシリアライズする。visitorIdが無ければパラメータ自体を含めない。
 * user_id（内部User UUID）と異なりSHA-256でハッシュしない。_sanu_vidは元々PIIを含まないランダムなuuidであり、ハッシュ化する理由がないため。
 */
export function serializeVisitorIdParam(visitorId: string | undefined): {
  visitor_id?: string;
} {
  return visitorId ? { visitor_id: visitorId } : {};
}

async function sha256Hex(str: string): Promise<string> {
  const buffer = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(str),
  );
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// useGtm() は現在の実験状態（experiments）という React state に依存するため、
// useMemo で構築し experiments が変わったときだけ新しい参照を返す。
export function useGtm() {
  const experiments = useExperiments();
  const visitorId = useVisitorId();
  return useMemo(
    () => ({
      sendEvent: (
        event: Exclude<
          GtmEventType,
          | "reservation_completed"
          | "reservation_updated"
          | "checkin_completed"
          | "checkout_completed"
          | "stay_review_submitted"
        >,
        values?: { [key: string]: string },
      ) => {
        // GTM イベントを送信
        sendGTMEvent({
          event,
          ...values,
          ...serializeExperimentsParam(experiments),
          ...serializeVisitorIdParam(visitorId),
          platform_context: "web",
        });
      },
      /**
       * GA4 の user_id を設定/解除する。dataLayer に `user_id` を push するのみ（イベントは伴わない）。
       * ログアウト時は `null` を push し、直前ユーザーの値を残さない。
       * 参考: https://developers.google.com/analytics/devguides/collection/ga4/user-id?client_type=gtm
       */
      setUserId: async (userId: string | null) => {
        const hashed = userId !== null ? await sha256Hex(userId) : null;
        sendGTMEvent({ user_id: hashed });
      },
      sendReservationCompletedEvent: ({
        provider,
        ticketMasterId,
        platform = "web",
      }: {
        provider: AccommodationReservationProvider;
        ticketMasterId?: string;
        platform?: "web" | "app";
      }) => {
        const { sec: leadTime, bucket: leadTimeBucket } = getLeadTime();
        let providerInfo: { [key: string]: string } = {
          provider: getProviderTypeName(provider),
        };
        if (
          provider.__typename === "StayTicketAccommodationReservationProvider"
        ) {
          providerInfo = {
            ...providerInfo,
            ticket_master_id: ticketMasterId ?? "",
          };
        }
        if (provider.__typename === "SanuIncAccommodationReservationProvider") {
          providerInfo = {
            ...providerInfo,
            usage: provider.usage ?? "",
          };
        }
        sendGTMEvent({
          event: "reservation_completed",
          ...providerInfo,
          platform_context: platform,
          time_to_reservation: leadTime?.toString(),
          time_to_reservation_bucket: leadTimeBucket,
          ...serializeExperimentsParam(experiments),
          ...serializeVisitorIdParam(visitorId),
        });
        clearLeadTimeStorage();
      },
      /**
       * 予約フォーム表示イベントを送信
       * リードタイム計測用の流入時刻を localStorage に保存する
       *
       * @param provider - 予約プロバイダ
       * @param mode - 新規予約か予約変更か
       * @param pastReservationCount - 過去の予約回数
       * @param platform - プラットフォーム
       */
      sendReservationFormViewedEvent: ({
        provider,
        mode,
        pastReservationCount,
        platform = "web",
      }: {
        provider: Pick<AccommodationReservationProvider, "__typename">;
        mode: "new" | "change";
        pastReservationCount?: number;
        platform?: "web" | "app";
      }) => {
        switch (provider.__typename) {
          case "OneMoreCabinAccommodationReservationProvider":
            // OneMoreCabinは利用する人と予約を完了させる人が違うので、計測しない
            break;
          default:
            recordReservationFormEntryTimeIfNeeded();
        }
        sendGTMEvent({
          event: mode === "new" ? "rsv_form_viewed" : "rsv_change_form_viewed",
          provider: getProviderTypeName(provider),
          past_reservation_count: pastReservationCount,
          ...serializeExperimentsParam(experiments),
          ...serializeVisitorIdParam(visitorId),
          platform_context: platform,
        });
      },
      sendCheckinCompletedEvent: ({
        provider,
      }: {
        provider: Pick<AccommodationReservationProvider, "__typename">;
      }) => {
        const providerName = getProviderTypeName(provider);
        sendGTMEvent({
          event: "checkin_completed",
          provider: providerName,
          platform_context: "web",
          ...serializeVisitorIdParam(visitorId),
        });
      },
      sendCheckoutCompletedEvent: ({
        provider,
      }: {
        provider: Pick<AccommodationReservationProvider, "__typename">;
      }) => {
        const providerName = getProviderTypeName(provider);
        sendGTMEvent({
          event: "checkout_completed",
          provider: providerName,
          platform_context: "web",
          ...serializeVisitorIdParam(visitorId),
        });
      },
      sendStayReviewSubmittedEvent: ({
        provider,
      }: {
        provider: Pick<AccommodationReservationProvider, "__typename">;
      }) => {
        const providerName = getProviderTypeName(provider);
        sendGTMEvent({
          event: "stay_review_submitted",
          provider: providerName,
          platform_context: "web",
          ...serializeVisitorIdParam(visitorId),
        });
      },
      sendVirtualPageView: (path: string, title?: string) => {
        sendGTMEvent({
          event: "virtual_page_view",
          page_path: path,
          page_location: window.location.origin + path,
          page_title: title ?? document.title,
          ...serializeExperimentsParam(experiments),
          ...serializeVisitorIdParam(visitorId),
        });
      },
    }),
    [experiments, visitorId],
  );
}
